import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async (token) => {
    try {
      const response = await api.get(`/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.log('Token validation failed:', error.response?.status);
      localStorage.removeItem('token');
      setUser(null);
      // Don't redirect here - let the API interceptor handle it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      // Use same normalization as register (trim + lowercase) so login matches stored email
      const normalizedEmail = (email || '').trim().toLowerCase();
      const response = await api.post(`/api/auth/login`, {
        email: normalizedEmail,
        password: password.trim()
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      return user;
    } catch (error) {
      console.error('Login error:', error.response?.data);
      
      // Handle rate limiting (429 errors)
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || 15 * 60;
        const minutes = Math.ceil(retryAfter / 60);
        setError(`Too many login attempts. Please wait ${minutes} minute${minutes !== 1 ? 's' : ''} before trying again.`);
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = error.response.data.errors.map(err => err.msg).join(', ');
        setError(`Validation error: ${validationErrors}`);
      } else {
        const message = error.response?.data?.message || 'An error occurred during login';
        setError(message);
      }
      throw error;
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      setError(null);
      // Use same normalization as login (trim + lowercase) so stored email matches login lookup
      const normalizedEmail = (email || '').trim().toLowerCase();
      const response = await api.post(`/api/auth/signup`, {
        name,
        email: normalizedEmail,
        password
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      return user;
    } catch (error) {
      const message = error.response?.data?.message || 'An error occurred during registration';
      setError(message);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout
  }), [user, loading, error, login, register, logout]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 